/**
 * What to paint a colour swatch with.
 *
 * A value's own `swatchHex` (set in the admin) always wins. Without one — and
 * an imported catalogue has none: SkyRush's 66 colour values arrived as bare
 * names — the swatch used to be a blank grey circle, so "Gold" and "Silver"
 * looked the same. This reads the name instead:
 *
 * - a known colour → its fill; metals (gold, silver, bronze, brass…) get a
 *   diagonal sheen rather than a flat tone, because on a buckle that is what
 *   the word means;
 * - two or more colours ("Gold/White", "Red & Yellow") → hard diagonal bands;
 * - a multicolour word ("Colour", "Multi") → a colour wheel; "Greyscale" → a
 *   black-to-white ramp;
 * - anything unrecognised → `null`, and the caller keeps its own fallback.
 *
 * The result is a CSS `background` value. Pure and client-safe.
 */

const SOLID: Record<string, string> = {
  black: "#1f1f1f",
  white: "#ffffff",
  grey: "#8a8a8a",
  gray: "#8a8a8a",
  red: "#c62828",
  pink: "#e88fb0",
  tan: "#d2b48c",
  yellow: "#f2c230",
  blue: "#2a5ea8",
  navy: "#1f2f4d",
  orange: "#e8772e",
  green: "#3f7d3a",
  brown: "#6b4423",
  purple: "#6a3d9a",
  turquoise: "#2bb3b1",
  cream: "#f3ead3",
  beige: "#d9c9a8",
  burgundy: "#7a1f2b",
  // Metals: the flat tone is used when a metal is one band of a split swatch.
  gold: "#c9a227",
  brass: "#b5a642",
  silver: "#c0c0c0",
  chrome: "#c8cacc",
  nickel: "#b7b9bc",
  pewter: "#8e9196",
  bronze: "#8c5a2b",
  copper: "#b87333",
};

const METAL_SHEEN: Record<string, string> = {
  gold: "linear-gradient(135deg, #f3dc8a 0%, #b8892b 55%, #e7c766 100%)",
  brass: "linear-gradient(135deg, #e6d98a 0%, #9c8a2e 55%, #d4c36a 100%)",
  silver: "linear-gradient(135deg, #f7f7f7 0%, #a9adb3 55%, #e3e5e8 100%)",
  chrome: "linear-gradient(135deg, #ffffff 0%, #9ea3a8 50%, #e9ebed 100%)",
  nickel: "linear-gradient(135deg, #eceded 0%, #9fa2a6 55%, #d8dadc 100%)",
  pewter: "linear-gradient(135deg, #c9cbce 0%, #74777c 55%, #b2b5b9 100%)",
  bronze: "linear-gradient(135deg, #d9a066 0%, #8c5a2b 55%, #c08447 100%)",
  copper: "linear-gradient(135deg, #e8a77c 0%, #a45a2a 55%, #d88e5e 100%)",
};

const MULTICOLOUR = new Set([
  "colour",
  "color",
  "multi",
  "multicolour",
  "multicolor",
  "multi-colour",
  "multi-color",
  "rainbow",
  "full colour",
  "full color",
]);
const GREYSCALE = new Set([
  "greyscale",
  "grayscale",
  "black and white",
  "black & white",
]);

const WHEEL =
  "conic-gradient(#e53935, #fdd835, #43a047, #1e88e5, #8e24aa, #e53935)";
const RAMP = "linear-gradient(135deg, #111111 0%, #eeeeee 100%)";

function normaliseHex(hex: string): string {
  return /^[0-9a-f]{3,8}$/i.test(hex) ? `#${hex}` : hex;
}

export function swatchBackground(
  value: string,
  hex?: string | null,
): string | null {
  if (hex) return normaliseHex(hex);

  const name = value.trim().toLowerCase().replace(/\s+/g, " ");
  if (!name) return null;
  if (MULTICOLOUR.has(name)) return WHEEL;
  if (GREYSCALE.has(name)) return RAMP;

  const parts = name.split(/\s*(?:\/|&|\+|,|\band\b)\s*/).filter(Boolean);
  if (parts.length === 1) return METAL_SHEEN[name] ?? SOLID[name] ?? null;

  const fills = parts.map((p) => SOLID[p]);
  if (fills.some((f) => f === undefined)) return null;
  // Hard stops: equal diagonal bands, one per colour.
  const step = 100 / fills.length;
  const stops = fills.flatMap((f, i) => [
    `${f} ${i * step}%`,
    `${f} ${(i + 1) * step}%`,
  ]);
  return `linear-gradient(135deg, ${stops.join(", ")})`;
}
